import { useEffect, useState } from "react";
import { Briefcase, Check, Loader2, Sparkles } from "lucide-react";
import { Button } from "../components/Button";
import { api, ApiError } from "../lib/api";
import type { PackSummary } from "../lib/me";

interface Props {
  onDone: () => void;
  onSkip: () => void;
}

export function StarterPackStep({ onDone, onSkip }: Props) {
  const [packs, setPacks] = useState<PackSummary[]>([]);
  const [loadingList, setLoadingList] = useState(true);
  const [installing, setInstalling] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    api
      .get<PackSummary[]>("/api/setup/packs")
      .then(setPacks)
      .catch(() => setPacks([]))
      .finally(() => setLoadingList(false));
  }, []);

  const install = async (packId: string) => {
    setError(null);
    setInstalling(packId);
    try {
      await api.post("/api/setup/install-pack", { packId });
      onDone();
    } catch (err) {
      const e = err as ApiError;
      if (e.code === "intents_exist") {
        // Already installed something — treat as success and move on.
        onDone();
        return;
      }
      setError(e.message ?? "Couldn't install pack.");
    } finally {
      setInstalling(null);
    }
  };

  if (loadingList) {
    return (
      <div className="flex items-center gap-2 px-2 py-6 text-sm text-gray-500">
        <Loader2 className="h-4 w-4 animate-spin" /> Loading packs…
      </div>
    );
  }

  return (
    <div className="space-y-5">
      <div>
        <h2 className="text-lg font-semibold text-gray-900">Step 2 — Pick a starter pack</h2>
        <p className="mt-1 text-sm text-gray-500">
          We'll create a set of categories with examples so the AI can classify your email out of the box.
          You can edit or add more later.
        </p>
      </div>

      <div className="space-y-3">
        {packs.map((p) => (
          <div
            key={p.packId}
            className="flex items-start justify-between gap-4 rounded-lg border border-gray-200 bg-white p-4"
          >
            <div className="flex items-start gap-3 flex-1 min-w-0">
              <div className="grid h-10 w-10 flex-shrink-0 place-items-center rounded-md bg-brand-100 text-brand-700">
                <Briefcase className="h-5 w-5" />
              </div>
              <div className="min-w-0 flex-1">
                <div className="flex items-baseline gap-2 flex-wrap">
                  <h3 className="text-sm font-semibold text-gray-900 truncate">{p.displayName}</h3>
                  <span className="text-xs text-gray-400 flex-shrink-0">{p.intentCount} categories</span>
                </div>
                <p className="mt-0.5 text-sm text-gray-500 break-words">{p.description}</p>
              </div>
            </div>
            <Button
              size="sm"
              className="flex-shrink-0"
              onClick={() => install(p.packId)}
              loading={installing === p.packId}
              disabled={installing !== null}
            >
              Start with Template
            </Button>
          </div>
        ))}

        <div className="flex items-start justify-between gap-4 rounded-lg border border-dashed border-gray-200 bg-gray-50 p-4">
          <div className="flex items-start gap-3 flex-1 min-w-0">
            <div className="grid h-10 w-10 flex-shrink-0 place-items-center rounded-md bg-gray-200 text-gray-600">
              <Sparkles className="h-5 w-5" />
            </div>
            <div className="min-w-0 flex-1">
              <h3 className="text-sm font-semibold text-gray-900">Start blank</h3>
              <p className="mt-0.5 text-sm text-gray-500 break-words">
                Skip the pack. Your inbox will receive emails but nothing will be classified until you
                add categories yourself.
              </p>
            </div>
          </div>
          <Button size="sm" variant="secondary" className="flex-shrink-0" onClick={onSkip} disabled={installing !== null}>
            Start with Scratch
          </Button>
        </div>
      </div>

      {error && (
        <div className="rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
          {error}
        </div>
      )}
    </div>
  );
}
